import React, { useState, useEffect } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Heading,
  HStack,
  Progress,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Text,
  VStack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import { buildApiUrl, createHeaders } from "../../services/apiClient";

export function PublicResultsPage({ surveyCode = "annual_industry" }) {
  const { t } = useTranslation("common");
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetch(buildApiUrl(`/surveys/admin/${surveyCode}/stats`), {
      headers: createHeaders({ json: true }),
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then(setStats)
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [surveyCode]);

  if (loading) {
    return (
      <VStack py={16}>
        <Spinner size="lg" color="gwbi.500" />
      </VStack>
    );
  }

  if (error) {
    return (
      <Alert status="error" borderRadius="md" maxW="600px" mx="auto" mt={8}>
        <AlertIcon />
        {error}
      </Alert>
    );
  }

  if (!stats) return null;

  const { totals, by_source, questions } = stats;

  return (
    <Box maxW="900px" mx="auto" py={10} px={4}>
      <VStack spacing={8} align="stretch">
        <VStack textAlign="center" spacing={2}>
          <Heading size="lg" color="gwbi.700">
            {t("results.title", "Annual Wine Industry Survey Results")}
          </Heading>
          <Text color="gray.500">
            {t("results.subtitle", "Aggregated and anonymized data from wine professionals worldwide")}
          </Text>
        </VStack>

        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4}>
          <StatCard label={t("results.responses", "Responses")} value={totals.total} />
          <StatCard label={t("results.completed", "Completed")} value={totals.completed} />
          <StatCard
            label={t("results.completion_rate", "Completion rate")}
            value={`${Math.round(totals.completion_rate * 100)}%`}
          />
          <StatCard
            label={t("results.in_progress", "In progress")}
            value={totals.in_progress}
          />
        </SimpleGrid>

        {Object.entries(by_source).length > 0 && (
          <Box bg="white" p={5} borderRadius="md" boxShadow="sm">
            <Heading size="sm" mb={4} color="gray.700">
              {t("results.by_source", "Responses by source")}
            </Heading>
            <VStack spacing={3} align="stretch">
              {Object.entries(by_source).map(([source, data]) => (
                <HStack key={source} justify="space-between">
                  <Text fontSize="sm" color="gray.600" textTransform="capitalize">
                    {source.replace(/_/g, " ")}
                  </Text>
                  {data.suppressed ? (
                    <Text fontSize="xs" color="gray.400" fontStyle="italic">
                      {t("results.suppressed", "N < 10 (suppressed)")}
                    </Text>
                  ) : (
                    <Text fontSize="sm" fontWeight="600">{data.completed}/{data.total}</Text>
                  )}
                </HStack>
              ))}
            </VStack>
          </Box>
        )}

        {Object.entries(questions).length > 0 && (
          <VStack spacing={4} align="stretch">
            <Heading size="sm" color="gray.700">
              {t("results.question_results", "Question-level results")}
            </Heading>
            {Object.entries(questions).map(([code, data]) => (
              <QuestionResult key={code} code={code} data={data} />
            ))}
          </VStack>
        )}
      </VStack>
    </Box>
  );
}

function StatCard({ label, value }) {
  return (
    <Stat
      bg="white"
      p={4}
      borderRadius="md"
      boxShadow="sm"
      borderLeft="3px solid"
      borderLeftColor="gwbi.500"
    >
      <StatLabel fontSize="xs" color="gray.500">{label}</StatLabel>
      <StatNumber fontSize="xl" color="gray.800">{value}</StatNumber>
    </Stat>
  );
}

function QuestionResult({ code, data }) {
  if (data.suppressed) {
    return (
      <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
        <Text fontSize="sm" fontWeight="600" color="gray.700" mb={1}>{code}</Text>
        <Text fontSize="xs" color="gray.400" fontStyle="italic">
          Insufficient responses (N={data.n}) — suppressed for privacy.
        </Text>
      </Box>
    );
  }

  return (
    <Box bg="white" p={4} borderRadius="md" boxShadow="sm">
      <HStack justify="space-between" mb={2}>
        <Text fontSize="sm" fontWeight="600" color="gray.700">{code}</Text>
        <Text fontSize="xs" color="gray.400">N={data.n}</Text>
      </HStack>
      {data.distribution && (
        <VStack spacing={2} align="stretch">
          {Object.entries(data.distribution).map(([key, pct]) => (
            <Box key={key}>
              <HStack justify="space-between" mb={1}>
                <Text fontSize="xs" color="gray.600">{key}</Text>
                <Text fontSize="xs" color="gray.500">{Math.round(pct * 100)}%</Text>
              </HStack>
              <Progress
                value={pct * 100}
                size="xs"
                colorScheme="gwbi"
                borderRadius="full"
              />
            </Box>
          ))}
        </VStack>
      )}
      {data.mean != null && (
        <SimpleGrid columns={4} spacing={2} mt={2}>
          <MiniStat label="Mean" value={data.mean} />
          <MiniStat label="Median" value={data.median} />
          <MiniStat label="Min" value={data.min} />
          <MiniStat label="Max" value={data.max} />
        </SimpleGrid>
      )}
      {data.response_count != null && !data.distribution && !data.mean && (
        <Text fontSize="xs" color="gray.500">{data.response_count} text responses collected</Text>
      )}
    </Box>
  );
}

function MiniStat({ label, value }) {
  return (
    <Box textAlign="center">
      <Text fontSize="xs" color="gray.400">{label}</Text>
      <Text fontSize="sm" fontWeight="600" color="gray.700">{value}</Text>
    </Box>
  );
}
